import React from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faClock, faPlug } from "@fortawesome/pro-regular-svg-icons";

import { Provider, Providers } from "src/api/connectApi/types";
import BigQueryIcon from "src/assets/BigQueryIcon.svg?react";
import BoniversumIcon from "src/assets/BoniversumIcon.svg?react";
import CockroachIcon from "src/assets/CockroachIcon.svg?react";
import CodatIcon from "src/assets/CodatIcon.svg?react";
import CreditsafeIcon from "src/assets/CreditsafeIcon.svg?react";
import CrifIcon from "src/assets/CrifIcon.svg?react";
import DataXIcon from "src/assets/DataXIcon.svg?react";
import DnbIcon from "src/assets/DnbIcon.svg?react";
import EquifaxIcon from "src/assets/EquifaxIcon.svg?react";
import ExperianIcon from "src/assets/ExperianIcon.svg?react";
import MongoDBIcon from "src/assets/MongoDBIcon.svg?react";
import MySQLIcon from "src/assets/MySQLIcon.svg?react";
import PostgresIcon from "src/assets/PostgresIcon.svg?react";
import RedshiftIcon from "src/assets/RedshiftIcon.svg?react";
import RetoolIcon from "src/assets/RetoolIcon.svg?react";
import SchufaIcon from "src/assets/SchufaIcon.svg?react";
import SnowflakeIcon from "src/assets/SnowflakeIcon.svg?react";
import { CustomIcon } from "src/connections/config/CustomIcon";
import { ManifestIcon } from "src/connections/config/manifest/ManifestIcon";
import {
  CONNECTIONS_MAP,
  providerInConfigMap,
} from "src/connections/connectionConfigs";
import { NodeIcon, NodeIconSize } from "src/design-system/NodeIcon";
import { assertUnreachable } from "src/utils/typeUtils";

export type IconSize = "xs" | "sm" | "md" | "lg" | "xl" | "xxl";

/** Shared by `custom` and `webhook`: uploaded logo in bordered frame, or default SVG. */
const ProviderDefaultOrUploadedLogo: React.FC<{
  mediaKey?: string | null;
  size: NodeIconSize;
  FallbackIcon: IconProp;
}> = ({ mediaKey, size, FallbackIcon }) => {
  if (!mediaKey) {
    return <NodeIcon icon={FallbackIcon} size={size} />;
  }

  return <CustomIcon size={size} mediaKey={mediaKey} />;
};

type Props = {
  provider: Provider | string;
  mediaKey?: string | null;
  size?: NodeIconSize;
};

export const ProviderIcon: React.FC<Props> = ({
  provider,
  mediaKey,
  size = "md",
}) => {
  // Handle manifest providers, this reads the icon from the manifest catalog
  // Converting the Providers array to string[] to please type checks
  if (!(Providers as readonly string[]).includes(provider)) {
    return <ManifestIcon size={size} provider={provider} />;
  }

  const staticProvider = provider as Provider;
  if (providerInConfigMap(staticProvider)) {
    const Icon = CONNECTIONS_MAP[staticProvider].icon;
    return <NodeIcon svg={Icon} size={size} />;
  }

  switch (staticProvider) {
    case "schufa": {
      return <NodeIcon svg={SchufaIcon} size={size} />;
    }
    case "boniversum": {
      return <NodeIcon svg={BoniversumIcon} size={size} />;
    }
    case "codat": {
      return <NodeIcon svg={CodatIcon} size={size} />;
    }
    case "data_x": {
      return <NodeIcon svg={DataXIcon} size={size} />;
    }
    case "dnb": {
      return <NodeIcon svg={DnbIcon} size={size} />;
    }
    case "crif_b2c": {
      return <NodeIcon svg={CrifIcon} size={size} />;
    }
    case "crif_b2b": {
      return <NodeIcon svg={CrifIcon} size={size} />;
    }
    case "crif_high_mark": {
      return <NodeIcon svg={CrifIcon} size={size} />;
    }
    case "equifax_uk": {
      return <NodeIcon svg={EquifaxIcon} size={size} />;
    }
    case "equifax_us": {
      return <NodeIcon svg={EquifaxIcon} size={size} />;
    }
    case "equifax_us_commercial_risk": {
      return <NodeIcon svg={EquifaxIcon} size={size} />;
    }
    case "experian_us_b2b": {
      return <NodeIcon svg={ExperianIcon} size={size} />;
    }
    case "experian_us": {
      return <NodeIcon svg={ExperianIcon} size={size} />;
    }
    case "experian_us_clarity_services": {
      return <NodeIcon svg={ExperianIcon} size={size} />;
    }
    case "creditsafe": {
      return <NodeIcon svg={CreditsafeIcon} size={size} />;
    }
    case "webhook": {
      return (
        <ProviderDefaultOrUploadedLogo
          FallbackIcon={faClock}
          mediaKey={mediaKey}
          size={size}
        />
      );
    }
    case "postgresql": {
      return <NodeIcon svg={PostgresIcon} size={size} />;
    }
    case "cockroachdb": {
      return <NodeIcon svg={CockroachIcon} size={size} />;
    }
    case "bigquery": {
      return <NodeIcon svg={BigQueryIcon} size={size} />;
    }
    case "snowflake": {
      return <NodeIcon svg={SnowflakeIcon} size={size} />;
    }
    case "redshift": {
      return <NodeIcon svg={RedshiftIcon} size={size} />;
    }
    case "mysql": {
      return <NodeIcon svg={MySQLIcon} size={size} />;
    }
    case "retool": {
      return <NodeIcon svg={RetoolIcon} size={size} />;
    }
    case "mongodb": {
      return <NodeIcon svg={MongoDBIcon} size={size} />;
    }
    case "custom": {
      return (
        <ProviderDefaultOrUploadedLogo
          FallbackIcon={faPlug}
          mediaKey={mediaKey}
          size={size}
        />
      );
    }
    default:
      assertUnreachable(staticProvider);
  }
};
